import { Controller } from "@hotwired/stimulus"

// Presentation only: the interval's worker owns time and phase transitions.
export default class extends Controller {
  static targets = ["edge", "marker"]

  connect() {
    this.animations = []
    this.motionPreference = window.matchMedia?.("(prefers-reduced-motion: reduce)")
    this.motionChanged = () => {
      this.cancelAnimations()
      if (this.frame) this.draw(this.frame, false)
    }
    this.motionPreference?.addEventListener("change", this.motionChanged)
  }

  disconnect() {
    this.reset()
    this.motionPreference?.removeEventListener("change", this.motionChanged)
  }

  position(phase, progress) {
    return [
      {left: "0%", top: `${100 * (1 - progress)}%`},
      {left: `${100 * progress}%`, top: "0%"},
      {left: "100%", top: `${100 * progress}%`},
      {left: `${100 * (1 - progress)}%`, top: "100%"}
    ][phase]
  }

  update(frame) {
    this.frame = frame
    this.draw(frame, frame.playing)
  }

  draw({phase, elapsed, durations}, playing) {
    this.cancelAnimations()
    if (!Number.isInteger(phase) || !(durations[phase] > 0)) return

    const progress = Math.min(1, Math.max(0, elapsed / durations[phase]))
    const previous = Math.max(0, (elapsed - 1) / durations[phase])
    this.edgeTargets.forEach((edge, index) => {
      const omitted = durations[index] === 0
      edge.classList.toggle("is-completed", !omitted && index < phase)
      edge.classList.toggle("is-active", index === phase)
      edge.classList.toggle("is-omitted", omitted)
      edge.style.setProperty("--phase-progress", index < phase && !omitted ? 1 : index === phase ? progress : 0)
    })
    Object.assign(this.markerTarget.style, this.position(phase, progress))
    if (!playing || this.motionPreference?.matches || !this.markerTarget.animate) return

    const marker = this.markerTarget.animate([
      this.position(phase, previous), this.position(phase, progress)
    ], {duration: 1000, easing: "linear", fill: "both"})
    const fill = this.edgeTargets[phase].querySelector(".breathing-square-fill")
    const scale = phase % 2 === 0 ? "scaleY" : "scaleX"
    const line = fill.animate([
      {transform: `${scale}(${previous})`}, {transform: `${scale}(${progress})`}
    ], {duration: 1000, easing: "linear", fill: "both"})
    this.animations = [marker, line]
  }

  pause() {
    this.animations?.forEach(animation => animation.pause())
  }

  cancelAnimations() {
    this.animations?.forEach(animation => animation.cancel())
    this.animations = []
  }

  reset() {
    this.cancelAnimations()
    this.frame = null
    this.edgeTargets.forEach(edge => {
      edge.classList.remove("is-active", "is-completed", "is-omitted")
      edge.style.setProperty("--phase-progress", 0)
    })
  }
}
